import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    type AggregateFailureEvidence,
    AggregateFailureEvidenceCard,
    ExpectedBehaviorCard,
    type ExpectSpec,
    GradingSpecCard,
    type InputTurn,
    JudgeAggregateBadges,
    type JudgeAggregateSummary,
    ModelInputCard,
    RepetitionCard,
    type RepetitionDetail,
    SourceCallCard,
    stringField,
} from '@apps/web/routes/_app/admin/voice-evals/case_report';
import { resolveCaseCostUsd } from '@apps/web/routes/_app/admin/voice-evals/matrix_summary';
import {
    CaseStatusBadge,
    EvidenceFamilyBadge,
    evidenceFamilyForCase,
    formatPercent,
    formatUsd,
    RunProvenanceCard,
} from '@apps/web/routes/_app/admin/voice-evals/shared';
import { useVoiceEvalCaseResult } from '@apps/web/swr_data_fetching/admin_voice_eval_queries';

export const Route = createFileRoute(
    '/_app/admin/voice-evals/runs/$runId/cases/$caseResultId',
)({
    head: () => ({
        meta: [{ title: 'Eval Case | Phoebe Admin' }],
    }),
    component: VoiceEvalCaseDetailPage,
});

function VoiceEvalCaseDetailPage() {
    const { runId, caseResultId } = Route.useParams();
    const { data, isLoading, error } = useVoiceEvalCaseResult(
        runId,
        caseResultId,
    );

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Spinner size="xl" tone="muted" />
            </div>
        );
    }
    if (error || !data) {
        return (
            <div className="text-muted-foreground p-8 text-center text-sm">
                Eval case result not found.
            </div>
        );
    }

    const { case: caseResult, detail, run } = data;
    const repetitions = (detail['runs'] ?? []) as Array<RepetitionDetail>;
    const failureEvidence = (detail['failure_evidence'] ??
        []) as Array<AggregateFailureEvidence>;
    const caseMetadata = (detail['case'] ?? {}) as Record<string, unknown>;
    const registryMetadata = (detail['registry'] ?? null) as Record<
        string,
        unknown
    > | null;
    const sourceCallId =
        stringField(caseMetadata, 'production_source_id') ??
        stringField(registryMetadata, 'production_source_id');
    const binaryJudgeSummary = detail[
        'binary_llm_judge'
    ] as JudgeAggregateSummary | null;
    const inputTurns = (detail['input_turns'] ??
        null) as ReadonlyArray<InputTurn> | null;
    const expect = (detail['expect'] ?? null) as ExpectSpec | null;
    const caseFamily = evidenceFamilyForCase(run.family, caseResult.kind);
    return (
        <div className="flex h-full flex-col gap-4 overflow-auto p-4">
            <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" asChild={true}>
                    <Link
                        to="/admin/voice-evals/runs/$runId"
                        params={{ runId }}
                    >
                        <ArrowLeft className="mr-1 h-4 w-4" />
                        Run
                    </Link>
                </Button>
                <Button variant="ghost" size="sm" asChild={true}>
                    <Link
                        to="/admin/voice-evals/cases"
                        search={{ registry_id: caseResult.registry_id }}
                    >
                        Case history
                    </Link>
                </Button>
            </div>
            <div className="flex flex-wrap items-center gap-3">
                <CaseStatusBadge caseResult={caseResult} />
                <EvidenceFamilyBadge family={caseFamily} />
                <h1 className="font-mono text-lg font-semibold">
                    {caseResult.case_name}
                </h1>
            </div>
            {caseFamily === 'prompt_only' && (
                <Card className="border-amber-300 bg-amber-50">
                    <CardContent className="py-3 text-sm text-amber-950">
                        This is a synthetic prompt/tool result. It does not
                        exercise telephony, the deployed voice pipeline, STT,
                        TTS, first audio, or post-call processing and is not a
                        service-health signal.
                    </CardContent>
                </Card>
            )}
            <div className="flex flex-wrap items-center gap-1.5">
                {caseResult.use_case && (
                    <Badge variant="outline">{caseResult.use_case}</Badge>
                )}
                {caseResult.harness && (
                    <Badge variant="secondary">{caseResult.harness}</Badge>
                )}
                {caseResult.kind && (
                    <Badge variant="secondary">{caseResult.kind}</Badge>
                )}
                {caseResult.status && (
                    <Badge
                        variant="outline"
                        className={
                            caseResult.status === 'known_gap'
                                ? 'text-amber-700'
                                : 'text-blue-700'
                        }
                    >
                        {caseResult.status}
                    </Badge>
                )}
                <span className="text-muted-foreground ml-2 text-xs">
                    {caseResult.module} · pass rate{' '}
                    {formatPercent(caseResult.pass_rate)} · known model estimate{' '}
                    {formatUsd(
                        resolveCaseCostUsd({
                            ...caseResult,
                            cost_accounting_version:
                                detail['cost_accounting_version'] === 2
                                    ? 2
                                    : null,
                        }),
                    )}
                    {detail['cost_tracking_complete'] === false &&
                        ' (partial; usage missing)'}
                </span>
            </div>
            {sourceCallId && <SourceCallCard sourceCallId={sourceCallId} />}
            <ModelInputCard inputTurns={inputTurns} />
            <GradingSpecCard expect={expect} />
            <ExpectedBehaviorCard caseMetadata={caseMetadata} />
            <AggregateFailureEvidenceCard evidence={failureEvidence} />
            {binaryJudgeSummary && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">
                            Judgment summary
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        <JudgeAggregateBadges
                            label="Binary judge"
                            summary={binaryJudgeSummary}
                        />
                    </CardContent>
                </Card>
            )}
            <RunProvenanceCard run={run} />
            <div className="space-y-3">
                {repetitions.length === 0 && (
                    <div className="text-muted-foreground py-8 text-center text-sm">
                        No per-repetition detail was recorded for this case.
                    </div>
                )}
                {repetitions.map((repetition, index) => (
                    <RepetitionCard
                        key={repetition.run_index ?? index}
                        repetition={repetition}
                        index={index}
                        family={caseFamily}
                        expect={expect}
                    />
                ))}
            </div>
        </div>
    );
}
